/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import {
  CleanIconButton,
  DropdownButton,
  Menu,
  Tooltip,
  Typography,
} from '@neo4j-ndl/react';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { CommonProps, HtmlAttributes } from '../../_common/types';
import ArrowSendIcon from '../../icons/generated/custom/ArrowSend';
import { ArrowSmallUpIconOutline } from '../../icons/generated/heroIcons/ArrowSmallUpIconOutline';
import { StopCircleIconOutline } from '../../icons/generated/heroIcons/StopCircleIconOutline';
import { XMarkIconOutline } from '../../icons/generated/heroIcons/XMarkIconOutline';
import { focusTextareaFromPromptWrapper } from './focusTextareaFromPromptWrapper';

const hasPromptContent = (inputValue: unknown): boolean => {
  if (typeof inputValue !== 'string') {
    return false;
  }

  return inputValue.trim().length > 0;
};

type PromptProps = {
  /** The prompt text */
  value?: React.ComponentPropsWithoutRef<'textarea'>['value'];
  /** Callback function called when the prompt text changes */
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  /** Maximum number of rows the textarea can expand to */
  maxRows?: number;
  /** Whether the textarea is disabled */
  isDisabled?: boolean;
  /** Whether the submit button is disabled */
  isSubmitDisabled?: boolean;
  /** Whether the submit button is "running". Shows a stop icon when true. */
  isRunningPrompt?: boolean;
  /** Callback function called when the submit button is clicked */
  onSubmitPrompt?: (
    event:
      | React.KeyboardEvent<HTMLTextAreaElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  /** Callback function called when the cancel button is clicked */
  onCancelPrompt?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => void;
  /** Content to display above the input card, e.g. a status message while a prompt is running */
  headerContent?: React.ReactNode;
  /** Content to display above the textarea */
  topContent?: React.ReactNode;
  /** Content to display below the textarea, on the leading side */
  bottomContent?: React.ReactNode;
  /** Content to display below the textarea, on the trailing side next to the submit button */
  bottomTrailingContent?: React.ReactNode;
  /** Disclaimer to display below the component */
  disclaimer?: React.ReactNode;
  /** Props for the textarea element */
  textareaProps?: HtmlAttributes<'textarea'>;
};

/**
 * The component is used to display the prompt input for an LLM.
 * It includes a textarea for the user to enter their prompt and a submit button.
 * It can also display content above and below the textarea, like uploaded files or a select for changing agents.
 *
 * @alpha - Changes to this component may be breaking.
 */
const PromptComponent = ({
  value,
  onChange,
  maxRows = 5,
  isDisabled = false,
  isRunningPrompt = false,
  onSubmitPrompt,
  onCancelPrompt,
  isSubmitDisabled = false,
  headerContent,
  topContent,
  bottomContent,
  bottomTrailingContent,
  disclaimer = 'All information should be verified.',
  className,
  style,
  htmlAttributes,
  ref,
  textareaProps,
  ...restProps
}: CommonProps<'div', PromptProps>) => {
  const classes = classNames('ndl-ai-prompt', className, {
    'ndl-disabled': isDisabled,
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaAboveRef = useRef<HTMLDivElement>(null);
  const [lineHeight, setLineHeight] = useState(0);

  /** File overflow stuff */
  useEffect(() => {
    let rafId: number | null = null;

    const checkOverflow = () => {
      if (textareaAboveRef.current !== null) {
        const element = textareaAboveRef.current;
        const maxHeight = parseFloat(
          getComputedStyle(element).maxHeight.replace('px', ''),
        );
        const scrollHeight = element.scrollHeight;

        // Computed max-height can be non-numeric (e.g. "none"), so bail safely.
        if (!Number.isFinite(maxHeight)) {
          element.classList.remove('ndl-can-scroll');
          return;
        }

        if (scrollHeight > maxHeight) {
          element.classList.add('ndl-can-scroll');
        } else {
          element.classList.remove('ndl-can-scroll');
        }
      }
    };

    const debouncedCheck = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(checkOverflow);
    };

    // Initial check
    checkOverflow();

    const resizeObserver = new ResizeObserver(debouncedCheck);
    const mutationObserver = new MutationObserver(debouncedCheck);

    if (textareaAboveRef.current !== null) {
      resizeObserver.observe(textareaAboveRef.current);
      mutationObserver.observe(textareaAboveRef.current, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  /** Textarea stuff */
  useEffect(() => {
    if (textareaRef.current !== null) {
      const computed = window.getComputedStyle(textareaRef.current);
      const parsedLineHeight = Number.parseFloat(computed.lineHeight);

      if (Number.isFinite(parsedLineHeight) && parsedLineHeight > 0) {
        setLineHeight(parsedLineHeight);
      } else {
        // Fallback for cases where line-height is reported as "normal".
        const parsedFontSize = Number.parseFloat(computed.fontSize);
        setLineHeight(
          Number.isFinite(parsedFontSize) && parsedFontSize > 0
            ? parsedFontSize * 1.2
            : 16,
        );
      }
    }
  }, []);

  const adjustTextareaHeight = useCallback(() => {
    const ta = textareaRef.current;

    if (ta !== null && lineHeight > 0) {
      // Reset height so scrollHeight measures correctly
      ta.style.height = 'auto';

      const maxHeight = lineHeight * maxRows;

      if (ta.scrollHeight <= maxHeight) {
        ta.style.overflowY = 'hidden';
        ta.style.height = `${ta.scrollHeight + 12}px`;
      } else {
        ta.style.overflowY = 'auto';
        ta.style.height = `${maxHeight + 12}px`;
      }
    }
  }, [lineHeight, maxRows]);

  // Adjust height when prompt value changes (including when cleared)
  useEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    adjustTextareaHeight();
    onChange?.(event);
  };

  const handleSubmitClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (hasPromptContent(textareaRef.current?.value)) {
      onSubmitPrompt?.(event);
    }
  };

  return (
    <div
      ref={ref}
      className={classes}
      style={style}
      {...restProps}
      {...htmlAttributes}
    >
      <div className="ndl-ai-prompt-wrapper">
        {Boolean(headerContent) && (
          <div className="ndl-ai-prompt-header">{headerContent}</div>
        )}
        <div
          className="ndl-ai-prompt-wrapper-inner"
          onMouseDownCapture={focusTextareaFromPromptWrapper}
        >
          {Boolean(topContent) && (
            <div
              className="ndl-ai-prompt-textarea-above"
              ref={textareaAboveRef}
            >
              {topContent}
            </div>
          )}
          <textarea
            placeholder="Ask anything"
            className="ndl-ai-prompt-textarea"
            rows={1}
            onChange={handleInput}
            value={value}
            ref={textareaRef}
            disabled={isDisabled}
            aria-label={textareaProps?.['aria-label'] ?? 'Prompt input'}
            onKeyDown={(event) => {
              const nativeEvent = event.nativeEvent as KeyboardEvent;
              const isComposing = nativeEvent.isComposing === true;

              if (event.key === 'Enter' && !isComposing && !event.shiftKey) {
                event.preventDefault();

                if (
                  hasPromptContent(event.currentTarget.value) &&
                  isSubmitDisabled !== true &&
                  isRunningPrompt !== true
                ) {
                  onSubmitPrompt?.(event);
                }
              }
            }}
            {...textareaProps}
          />
          <div className="ndl-ai-prompt-textarea-below">
            {Boolean(bottomContent) && (
              <div className="ndl-ai-prompt-textarea-below-leading">
                {bottomContent}
              </div>
            )}
            <div className="ndl-ai-prompt-textarea-below-trailing">
              {bottomTrailingContent}
              <SubmitPromptButton
                isDisabled={isSubmitDisabled}
                isRunningPrompt={isRunningPrompt}
                onSubmit={handleSubmitClick}
                onCancel={onCancelPrompt}
              />
            </div>
          </div>
        </div>
      </div>
      {Boolean(disclaimer) && (
        <Typography variant="body-small" className="ndl-ai-prompt-footer">
          {disclaimer}
        </Typography>
      )}
    </div>
  );
};

const SubmitPromptButton = ({
  onSubmit,
  onCancel,
  isDisabled,
  isRunningPrompt,
}: {
  isDisabled?: boolean;
  isRunningPrompt?: boolean;
  onSubmit?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onCancel?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
  return (
    <CleanIconButton
      className="ndl-ai-prompt-submit-button"
      description={isRunningPrompt === true ? 'Stop' : 'Send'}
      size="small"
      isDisabled={isDisabled}
      onClick={isRunningPrompt === true ? onCancel : onSubmit}
    >
      {isRunningPrompt === true ? (
        <StopCircleIconOutline />
      ) : (
        <ArrowSmallUpIconOutline />
      )}
    </CleanIconButton>
  );
};

type AgentSelectProps = {
  value: { label: React.ReactNode; value: string };
  options: Array<{ label: React.ReactNode; value: string }>;
  onChange: (option: { label: React.ReactNode; value: string }) => void;
};

const AgentSelect = ({ value, options = [], onChange }: AgentSelectProps) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const displayLabel = value?.label ?? options[0]?.label ?? '';

  const handleItemClick = (option: {
    label: React.ReactNode;
    value: string;
  }) => {
    onChange?.(option);
    setIsMenuOpen(false);
  };

  return (
    <>
      <DropdownButton
        size="small"
        className="ndl-ai-prompt-dropdown-button"
        onClick={() => setIsMenuOpen((old) => !old)}
        isOpen={isMenuOpen}
        ref={anchorRef}
      >
        {displayLabel}
      </DropdownButton>
      <Menu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        anchorRef={anchorRef}
        placement="top-start-bottom-start"
      >
        {options.map((option) => (
          <Menu.RadioItem
            key={option.value}
            title={option.label}
            isChecked={value?.value === option.value}
            onClick={() => handleItemClick(option)}
          />
        ))}
      </Menu>
    </>
  );
};

type ContextTagProps = {
  children?: string;
  onClose: () => void;
};

const ContextTag = ({ children, onClose }: ContextTagProps) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isRemoveActive, setIsRemoveActive] = useState(false);

  return (
    <Tooltip
      type="simple"
      isOpen={isTooltipOpen || isRemoveActive}
      onOpenChange={(isOpen) => {
        if (isOpen && !isRemoveActive) {
          const isTruncated =
            textRef.current?.scrollWidth &&
            textRef.current?.clientWidth &&
            textRef.current.scrollWidth > textRef.current.clientWidth;
          if (isTruncated) {
            setIsTooltipOpen(true);
          }
        } else if (!isRemoveActive) {
          setIsTooltipOpen(false);
        }
      }}
      hoverDelay={{ close: 0, open: 500 }}
    >
      <Tooltip.Trigger hasButtonWrapper>
        <div className="ndl-ai-prompt-context-tag">
          <ArrowSendIcon className="ndl-ai-prompt-context-tag-icon" />
          <Typography
            variant="body-medium"
            className="ndl-ai-prompt-context-tag-text"
            ref={textRef}
          >
            {children}
          </Typography>

          <button
            type="button"
            onClick={onClose}
            aria-label={`Remove ${children}`}
            onMouseEnter={() => setIsRemoveActive(true)}
            onMouseLeave={() => setIsRemoveActive(false)}
            onFocus={() => setIsRemoveActive(true)}
            onBlur={() => setIsRemoveActive(false)}
            className="ndl-ai-prompt-context-tag-close-button"
          >
            <XMarkIconOutline className="ndl-ai-prompt-context-tag-icon" />
          </button>
        </div>
      </Tooltip.Trigger>
      <Tooltip.Content>
        {isRemoveActive ? `Remove ${children}` : children}
      </Tooltip.Content>
    </Tooltip>
  );
};

const Prompt = Object.assign(PromptComponent, {
  ContextTag: ContextTag,
  Select: AgentSelect,
});

export { Prompt };
